import React, { useRef, useState, useEffect } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select, { MultiValue } from 'react-select';
import {
    fetchStates,
    fetchCampaignSearchFilters,
    fetchCampaignMetadata,
} from '~/services/campaign';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import { COLUMNS_BY_GROUP, DEFAULT_VISIBLE_COLUMNS, isDefaultSelection, parseVisibleColumns } from './campaignColumns';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface StateOption {
    value: string;
    label: string;
}

interface CampaignStatusOption {
    value: string;
    label: string;
}

// A diagnosis plus the condition it belongs to, so the Diagnosis dropdown can
// be filtered by the selected conditions.
interface MedicalIssueOption extends StateOption {
    medicalReasonId: string;
}

// The campaign milestone dates that can be searched as a range, so a report can
// ask "which campaigns became disbursable in June?" -- a question the status
// filter alone can't answer, since a campaign that opened in June may be closed
// by now (HHLTB-2125).
const STATUS_DATE_FIELD_OPTIONS: StateOption[] = [
    { value: 'created', label: 'Created' },
    { value: 'unpublished', label: 'Unpublished' },
    { value: 'pend_financial_need', label: 'Pending Financial Need' },
    { value: 'approved', label: 'Approved / Open' },
    { value: 'funded', label: 'Funded' },
    { value: 'disbursable', label: 'Disbursable' },
    { value: 'closed', label: 'Closed' },
    { value: 'reopened', label: 'Re-opened' },
    { value: 'first_donation', label: 'First Donation' },
];

interface StateApiResponse {
    abbreviation: string;
    state: string;
}

interface CampaignStatusApiResponse {
    code: string;
    name: string;
}

// Date range preset options
interface DatePreset {
    value: string;
    label: string;
    getRange: () => { start: string; end: string };
}

const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

// Date preset options for react-select
const datePresetSelectOptions: StateOption[] = [
    { value: '', label: 'Select date range' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
];

const getDatePresets = (): DatePreset[] => {
    const today = new Date();

    return [
        {
            value: '',
            label: 'Select date range',
            getRange: () => ({ start: '', end: '' }),
        },
        {
            value: 'last_7_days',
            label: 'Last 7 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 7);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_30_days',
            label: 'Last 30 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_month',
            label: 'This Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_month',
            label: 'Last Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth() - 1,
                    1,
                );
                const end = new Date(today.getFullYear(), today.getMonth(), 0);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_quarter',
            label: 'This Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const start = new Date(
                    today.getFullYear(),
                    currentQuarter * 3,
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_quarter',
            label: 'Last Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const lastQuarter = currentQuarter - 1;
                const year =
                    lastQuarter < 0
                        ? today.getFullYear() - 1
                        : today.getFullYear();
                const quarter = lastQuarter < 0 ? 3 : lastQuarter;
                const start = new Date(year, quarter * 3, 1);
                const end = new Date(year, quarter * 3 + 3, 0);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_year',
            label: 'This Year',
            getRange: () => {
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_year',
            label: 'Last Year',
            getRange: () => {
                const start = new Date(today.getFullYear() - 1, 0, 1);
                const end = new Date(today.getFullYear() - 1, 11, 31);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'current_fiscal_year',
            label: 'Current Fiscal Year',
            getRange: () => {
                const fyStartYear =
                    today.getMonth() >= 9
                        ? today.getFullYear()
                        : today.getFullYear() - 1;
                const start = new Date(fyStartYear, 9, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
    ];
};

const EMPTY_FORM_VALUES = {
    ntaf_id: '',
    campaign_created_start_date: '',
    campaign_created_end_date: '',
    privacy: '',
    zip_code: '',
    city: '',
    status_date_field: '',
    status_date_start: '',
    status_date_end: '',
    csc: '',
    special_circumstance: '',
    living_donor: '',
    deceased: '',
    funds_raised_start_date: '',
    funds_raised_end_date: '',
    campaign_funded_start_date: '',
    campaign_funded_end_date: '',
    funds_raised_min: '',
    funds_raised_max: '',
    unique_donors_min: '',
    unique_donors_max: '',
    dob_start_date: '',
    dob_end_date: '',
    age_min: '',
    age_max: '',
};

const CampaignAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();
    const [states, setStates] = useState<StateOption[]>([]);
    const [selectedStates, setSelectedStates] = useState<StateOption[]>([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [campaignStatusOptions, setCampaignStatusOptions] = useState<
        CampaignStatusOption[]
    >([]);
    const [selectedCampaignStatuses, setSelectedCampaignStatuses] = useState<
        CampaignStatusOption[]
    >([]);
    const [isLoadingCampaignStatuses, setIsLoadingCampaignStatuses] =
        useState(false);
    const [cscOptions, setCscOptions] = useState<StateOption[]>([]);
    const [selectedCsc, setSelectedCsc] = useState<StateOption | null>(null);
    const [isLoadingCscs, setIsLoadingCscs] = useState(false);
    const [medicalReasonOptions, setMedicalReasonOptions] = useState<
        StateOption[]
    >([]);
    const [selectedMedicalReasons, setSelectedMedicalReasons] = useState<
        StateOption[]
    >([]);
    // Every diagnosis, keyed back to its condition so the Diagnosis dropdown
    // can narrow to whatever conditions are selected.
    const [medicalIssueOptions, setMedicalIssueOptions] = useState<
        MedicalIssueOption[]
    >([]);
    const [selectedMedicalIssues, setSelectedMedicalIssues] = useState<
        StateOption[]
    >([]);
    // Referral source: a display column since HHLTB-2125's first pass, now a
    // filter too so "everyone referred by X" is one search, not a CSV sort.
    const [referredByOptions, setReferredByOptions] = useState<StateOption[]>([]);
    const [selectedReferredBy, setSelectedReferredBy] = useState<StateOption[]>(
        [],
    );
    const [ethnicityOptions, setEthnicityOptions] = useState<StateOption[]>([]);
    const [selectedEthnicities, setSelectedEthnicities] = useState<StateOption[]>(
        [],
    );

    // Legacy status options (hardcoded from Campaign.CAMPAIGN_STATUSES)
    const legacyStatusOptions: CampaignStatusOption[] = [
        { value: 'Default', label: 'Default' },
        { value: 'Open', label: 'Open' },
        { value: 'Complete', label: 'Complete' },
        { value: 'Re-opened', label: 'Re-opened' },
        { value: 'Closed', label: 'Closed' },
    ];
    const [selectedLegacyStatuses, setSelectedLegacyStatuses] = useState<
        CampaignStatusOption[]
    >([]);
    const [selectedCreatedDatePreset, setSelectedCreatedDatePreset] =
        useState('');
    const [selectedFundedDatePreset, setSelectedFundedDatePreset] =
        useState('');
    const [selectedFundsRaisedDatePreset, setSelectedFundsRaisedDatePreset] =
        useState('');
    const [selectedStatusDatePreset, setSelectedStatusDatePreset] =
        useState('');
    const datePresets = getDatePresets();
    const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

    // Column picker: seed from the URL so "Back to Search" and saved-report
    // round-trips pre-fill the selection; otherwise fall back to defaults.
    const [showColumns, setShowColumns] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
        );
    };

    useEffect(() => {
        const loadStates = async () => {
            setIsLoadingStates(true);
            try {
                const statesData = await fetchStates();
                const stateOptions = statesData.map(
                    (state: StateApiResponse) => ({
                        value: state.abbreviation,
                        label: state.state,
                    }),
                );
                setStates(stateOptions);
            } catch (error) {
                console.error('Error fetching states:', error);
            } finally {
                setIsLoadingStates(false);
            }
        };

        const loadCampaignStatuses = async () => {
            setIsLoadingCampaignStatuses(true);
            try {
                const filtersData = await fetchCampaignSearchFilters();
                const statusOptions = filtersData.campaign_statuses.map(
                    (status: CampaignStatusApiResponse) => ({
                        value: status.code,
                        label: status.name,
                    }),
                );
                setCampaignStatusOptions(statusOptions);
            } catch (error) {
                console.error('Error fetching campaign statuses:', error);
            } finally {
                setIsLoadingCampaignStatuses(false);
            }
        };

        // CSCs and ethnicities both come from the campaign metadata endpoint,
        // so one request populates both dropdowns.
        const loadMetadata = async () => {
            setIsLoadingCscs(true);
            try {
                const metadata = await fetchCampaignMetadata();
                if (metadata?.patient_services_coordinators) {
                    const options = metadata.patient_services_coordinators.map(
                        (psc: { id: number; full_name: string }) => ({
                            value: String(psc.id),
                            label: psc.full_name,
                        }),
                    );
                    setCscOptions(options);
                }
                if (metadata?.medical_reasons) {
                    setMedicalReasonOptions(
                        metadata.medical_reasons.map(
                            (reason: { id: number; name: string }) => ({
                                value: String(reason.id),
                                label: reason.name,
                            }),
                        ),
                    );
                }
                if (metadata?.medical_issues) {
                    setMedicalIssueOptions(
                        metadata.medical_issues.map(
                            (issue: {
                                id: number;
                                name: string;
                                medical_reason?: { id: number } | null;
                            }) => ({
                                value: String(issue.id),
                                label: issue.name,
                                medicalReasonId: issue.medical_reason
                                    ? String(issue.medical_reason.id)
                                    : '',
                            }),
                        ),
                    );
                }
                if (metadata?.heard_of_sources) {
                    setReferredByOptions(
                        metadata.heard_of_sources.map(
                            (source: { id: number; name: string }) => ({
                                value: String(source.id),
                                label: source.name,
                            }),
                        ),
                    );
                }
                if (metadata?.ethnicities) {
                    setEthnicityOptions(
                        metadata.ethnicities
                            .filter((ethnicity: string) => ethnicity)
                            .map((ethnicity: string) => ({
                                value: ethnicity,
                                label: ethnicity,
                            })),
                    );
                }
            } catch (error) {
                console.error('Error fetching campaign metadata:', error);
            } finally {
                setIsLoadingCscs(false);
            }
        };

        loadStates();
        loadCampaignStatuses();
        loadMetadata();
    }, []);

    // Populate form with URL parameters when component loads
    useEffect(() => {
        if (searchParams.size > 0) {
            // Populate form values
            setFormValues({
                ntaf_id: searchParams.get('ntaf_id') || '',
                campaign_created_start_date:
                    searchParams.get('campaign_created_start_date') || '',
                campaign_created_end_date:
                    searchParams.get('campaign_created_end_date') || '',
                privacy: searchParams.get('privacy') || '',
                zip_code: searchParams.get('zip_code') || '',
                city: searchParams.get('city') || '',
                status_date_field: searchParams.get('status_date_field') || '',
                status_date_start: searchParams.get('status_date_start') || '',
                status_date_end: searchParams.get('status_date_end') || '',
                csc: searchParams.get('csc') || '',
                special_circumstance:
                    searchParams.get('special_circumstance') || '',
                living_donor: searchParams.get('living_donor') || '',
                deceased: searchParams.get('deceased') || '',
                funds_raised_start_date:
                    searchParams.get('funds_raised_start_date') || '',
                funds_raised_end_date:
                    searchParams.get('funds_raised_end_date') || '',
                campaign_funded_start_date:
                    searchParams.get('campaign_funded_start_date') || '',
                campaign_funded_end_date:
                    searchParams.get('campaign_funded_end_date') || '',
                funds_raised_min: searchParams.get('funds_raised_min') || '',
                funds_raised_max: searchParams.get('funds_raised_max') || '',
                unique_donors_min: searchParams.get('unique_donors_min') || '',
                unique_donors_max: searchParams.get('unique_donors_max') || '',
                dob_start_date: searchParams.get('dob_start_date') || '',
                dob_end_date: searchParams.get('dob_end_date') || '',
                age_min: searchParams.get('age_min') || '',
                age_max: searchParams.get('age_max') || '',
            });

            // Populate selected states
            const stateParams = searchParams.getAll('state');
            if (stateParams.length > 0 && states.length > 0) {
                const selectedStateOptions = states.filter((state) =>
                    stateParams.includes(state.value),
                );
                setSelectedStates(selectedStateOptions);
            }

            // Populate selected campaign statuses
            const campaignStatusParams = searchParams.getAll('campaign_status');
            if (
                campaignStatusParams.length > 0 &&
                campaignStatusOptions.length > 0
            ) {
                const selectedStatusOptions = campaignStatusOptions.filter(
                    (status) => campaignStatusParams.includes(status.value),
                );
                setSelectedCampaignStatuses(selectedStatusOptions);
            }

            // Populate selected legacy statuses
            const legacyStatusParams = searchParams.getAll('legacy_status');
            if (legacyStatusParams.length > 0) {
                const selectedLegacyOptions = legacyStatusOptions.filter(
                    (status) => legacyStatusParams.includes(status.value),
                );
                setSelectedLegacyStatuses(selectedLegacyOptions);
            }

            // Populate selected CSC
            const cscParam = searchParams.get('csc');
            if (cscParam && cscOptions.length > 0) {
                const matchedCsc = cscOptions.find(
                    (opt) => opt.value === cscParam,
                );
                if (matchedCsc) {
                    setSelectedCsc(matchedCsc);
                }
            }

            // Populate selected conditions and diagnoses
            const medicalReasonParams = searchParams.getAll('medical_reason');
            if (
                medicalReasonParams.length > 0 &&
                medicalReasonOptions.length > 0
            ) {
                setSelectedMedicalReasons(
                    medicalReasonOptions.filter((option) =>
                        medicalReasonParams.includes(option.value),
                    ),
                );
            }

            const medicalIssueParams = searchParams.getAll('medical_issue');
            if (
                medicalIssueParams.length > 0 &&
                medicalIssueOptions.length > 0
            ) {
                setSelectedMedicalIssues(
                    medicalIssueOptions.filter((option) =>
                        medicalIssueParams.includes(option.value),
                    ),
                );
            }

            const referredByParams = searchParams.getAll('referred_by');
            if (referredByParams.length > 0 && referredByOptions.length > 0) {
                setSelectedReferredBy(
                    referredByOptions.filter((option) =>
                        referredByParams.includes(option.value),
                    ),
                );
            }

            // Populate selected ethnicities
            const ethnicityParams = searchParams.getAll('ethnicity');
            if (ethnicityParams.length > 0 && ethnicityOptions.length > 0) {
                setSelectedEthnicities(
                    ethnicityOptions.filter((option) =>
                        ethnicityParams.includes(option.value),
                    ),
                );
            }
        }
    }, [
        searchParams,
        states,
        campaignStatusOptions,
        cscOptions,
        ethnicityOptions,
        medicalReasonOptions,
        medicalIssueOptions,
        referredByOptions,
    ]);

    // Diagnoses narrow to the selected conditions; with no condition picked the
    // full list stays available.
    const availableMedicalIssues = selectedMedicalReasons.length
        ? medicalIssueOptions.filter((issue) =>
              selectedMedicalReasons.some(
                  (reason) => reason.value === issue.medicalReasonId,
              ),
          )
        : medicalIssueOptions;

    const handleMedicalReasonChange = (
        selectedOptions: MultiValue<StateOption>,
    ) => {
        const reasons = [...selectedOptions];
        setSelectedMedicalReasons(reasons);
        // Drop any diagnosis that no longer belongs to a selected condition,
        // so the form can't submit a contradictory pair.
        if (reasons.length) {
            const reasonIds = reasons.map((reason) => reason.value);
            setSelectedMedicalIssues((prev) =>
                prev.filter((issue) => {
                    const match = medicalIssueOptions.find(
                        (opt) => opt.value === issue.value,
                    );
                    return !!match && reasonIds.includes(match.medicalReasonId);
                }),
            );
        }
    };

    const handleMedicalIssueChange = (
        selectedOptions: MultiValue<StateOption>,
    ) => {
        setSelectedMedicalIssues([...selectedOptions]);
    };

    const handleStateChange = (selectedOptions: MultiValue<StateOption>) => {
        setSelectedStates([...selectedOptions]);
    };

    const handleEthnicityChange = (
        selectedOptions: MultiValue<StateOption>,
    ) => {
        setSelectedEthnicities([...selectedOptions]);
    };

    const handleCampaignStatusChange = (
        selectedOptions: MultiValue<CampaignStatusOption>,
    ) => {
        setSelectedCampaignStatuses([...selectedOptions]);
    };

    const handleLegacyStatusChange = (
        selectedOptions: MultiValue<CampaignStatusOption>,
    ) => {
        setSelectedLegacyStatuses([...selectedOptions]);
    };

    // Every filter on this form is either a formValues key or one of these
    // selections, so the two together answer "is anything set?".
    const hasActiveFilters =
        Object.values(formValues).some((value) => value !== '') ||
        selectedStates.length > 0 ||
        selectedCampaignStatuses.length > 0 ||
        selectedLegacyStatuses.length > 0 ||
        selectedMedicalReasons.length > 0 ||
        selectedMedicalIssues.length > 0 ||
        selectedReferredBy.length > 0 ||
        selectedEthnicities.length > 0 ||
        selectedCsc !== null;

    const handleResetFilters = () => {
        setFormValues(EMPTY_FORM_VALUES);
        setSelectedStates([]);
        setSelectedCampaignStatuses([]);
        setSelectedLegacyStatuses([]);
        setSelectedMedicalReasons([]);
        setSelectedMedicalIssues([]);
        setSelectedReferredBy([]);
        setSelectedEthnicities([]);
        setSelectedCsc(null);
        setSelectedCreatedDatePreset('');
        setSelectedFundedDatePreset('');
        setSelectedFundsRaisedDatePreset('');
        setSelectedStatusDatePreset('');
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget as HTMLFormElement);
        const searchParams = new URLSearchParams();

        // Add regular form fields
        const ntafId = formData.get('ntaf_id') as string;
        if (ntafId?.trim()) searchParams.append('ntaf_id', ntafId);

        const campaignCreatedStartDate = formData.get(
            'campaign_created_start_date',
        ) as string;
        if (campaignCreatedStartDate)
            searchParams.append(
                'campaign_created_start_date',
                campaignCreatedStartDate,
            );

        const campaignCreatedEndDate = formData.get(
            'campaign_created_end_date',
        ) as string;
        if (campaignCreatedEndDate)
            searchParams.append(
                'campaign_created_end_date',
                campaignCreatedEndDate,
            );

        const privacy = formData.get('privacy') as string;
        if (privacy) searchParams.append('privacy', privacy);

        const zipCode = formData.get('zip_code') as string;
        if (zipCode?.trim()) searchParams.append('zip_code', zipCode);

        const city = formData.get('city') as string;
        if (city?.trim()) searchParams.append('city', city.trim());

        // The range is meaningless without a field to apply it to, so only one
        // of the two states can send anything on its own.
        if (
            formValues.status_date_field &&
            (formValues.status_date_start || formValues.status_date_end)
        ) {
            searchParams.append(
                'status_date_field',
                formValues.status_date_field,
            );
            if (formValues.status_date_start)
                searchParams.append(
                    'status_date_start',
                    formValues.status_date_start,
                );
            if (formValues.status_date_end)
                searchParams.append(
                    'status_date_end',
                    formValues.status_date_end,
                );
        }

        selectedMedicalReasons.forEach((reason) => {
            searchParams.append('medical_reason', reason.value);
        });

        selectedMedicalIssues.forEach((issue) => {
            searchParams.append('medical_issue', issue.value);
        });

        selectedReferredBy.forEach((source) => {
            searchParams.append('referred_by', source.value);
        });

        if (selectedCsc) {
            searchParams.append('csc', selectedCsc.value);
        }

        const specialCircumstance = formData.get(
            'special_circumstance',
        ) as string;
        if (specialCircumstance)
            searchParams.append('special_circumstance', specialCircumstance);

        const livingDonor = formData.get('living_donor') as string;
        if (livingDonor) searchParams.append('living_donor', livingDonor);
        const deceased = formData.get('deceased') as string;
        if (deceased) searchParams.append('deceased', deceased);

        const fundsRaisedStartDate = formData.get(
            'funds_raised_start_date',
        ) as string;
        if (fundsRaisedStartDate)
            searchParams.append(
                'funds_raised_start_date',
                fundsRaisedStartDate,
            );

        const fundsRaisedEndDate = formData.get(
            'funds_raised_end_date',
        ) as string;
        if (fundsRaisedEndDate)
            searchParams.append('funds_raised_end_date', fundsRaisedEndDate);

        const campaignFundedStartDate = formData.get(
            'campaign_funded_start_date',
        ) as string;
        if (campaignFundedStartDate)
            searchParams.append(
                'campaign_funded_start_date',
                campaignFundedStartDate,
            );

        const campaignFundedEndDate = formData.get(
            'campaign_funded_end_date',
        ) as string;
        if (campaignFundedEndDate)
            searchParams.append(
                'campaign_funded_end_date',
                campaignFundedEndDate,
            );

        const fundsRaisedMin = formData.get('funds_raised_min') as string;
        if (fundsRaisedMin?.trim())
            searchParams.append('funds_raised_min', fundsRaisedMin);

        const fundsRaisedMax = formData.get('funds_raised_max') as string;
        if (fundsRaisedMax?.trim())
            searchParams.append('funds_raised_max', fundsRaisedMax);

        const uniqueDonorsMin = formData.get('unique_donors_min') as string;
        if (uniqueDonorsMin?.trim())
            searchParams.append('unique_donors_min', uniqueDonorsMin);

        const uniqueDonorsMax = formData.get('unique_donors_max') as string;
        if (uniqueDonorsMax?.trim())
            searchParams.append('unique_donors_max', uniqueDonorsMax);

        const dobStartDate = formData.get('dob_start_date') as string;
        if (dobStartDate) searchParams.append('dob_start_date', dobStartDate);

        const dobEndDate = formData.get('dob_end_date') as string;
        if (dobEndDate) searchParams.append('dob_end_date', dobEndDate);

        const ageMin = formData.get('age_min') as string;
        if (ageMin?.trim()) searchParams.append('age_min', ageMin);

        const ageMax = formData.get('age_max') as string;
        if (ageMax?.trim()) searchParams.append('age_max', ageMax);

        // Add selected ethnicities
        selectedEthnicities.forEach((ethnicity) => {
            searchParams.append('ethnicity', ethnicity.value);
        });

        // Add selected states
        selectedStates.forEach((state) => {
            searchParams.append('state', state.value);
        });

        // Add selected campaign statuses
        selectedCampaignStatuses.forEach((status) => {
            searchParams.append('campaign_status', status.value);
        });

        // Add selected legacy statuses
        selectedLegacyStatuses.forEach((status) => {
            searchParams.append('legacy_status', status.value);
        });

        // Append column selection only when it differs from the defaults,
        // keeping the common-case URL clean. Single comma-joined param to
        // match how the results page and saved-report replay read it.
        if (!isDefaultSelection(selectedColumns) && selectedColumns.length) {
            searchParams.append('visible_columns', selectedColumns.join(','));
        }

        // Save filters to context and navigate to results
        setFilterParams(searchParams.toString());
        setActiveAccordion(1);
        navigate(`/reports/results?${searchParams.toString()}`);
    };

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Campaign
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus
                        className={`${active === id ? 'block' : 'hidden'}`}
                    />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='w-full'>
                        <h2 className='text-[#949599] text-[18px/22px]'>
                            FILTERS
                        </h2>

                        {/* Row 1: NTAF ID and Campaign Status */}
                        <div className='mt-4 flex flex-col gap-4 lg:flex-row'>
                            <div className='w-full lg:w-48'>
                                <label
                                    htmlFor='ntaf-id'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    NTAF ID
                                </label>
                                <input
                                    type='number'
                                    id='ntaf-id'
                                    name='ntaf_id'
                                    placeholder='Enter NTAF ID'
                                    value={formValues.ntaf_id}
                                    onChange={(e) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            ntaf_id: e.target.value,
                                        }))
                                    }
                                    className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                            <div className='w-full lg:w-72'>
                                <label
                                    htmlFor='campaign-status'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Campaign Status
                                </label>
                                <Select
                                    id='campaign-status'
                                    menuPlacement='auto'
                                    options={campaignStatusOptions}
                                    isMulti
                                    onChange={handleCampaignStatusChange}
                                    value={selectedCampaignStatuses}
                                    isLoading={isLoadingCampaignStatuses}
                                    placeholder='Select statuses...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div className='w-full lg:w-64'>
                                <label
                                    htmlFor='legacy-status'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Legacy Status
                                </label>
                                <Select
                                    id='legacy-status'
                                    menuPlacement='auto'
                                    options={legacyStatusOptions}
                                    isMulti
                                    onChange={handleLegacyStatusChange}
                                    value={selectedLegacyStatuses}
                                    placeholder='Select statuses...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 2: Location filters */}
                        <div className='mt-4 flex flex-col gap-4 lg:flex-row'>
                            <div className='w-full lg:w-48'>
                                <label
                                    htmlFor='region'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Region
                                </label>
                                <input
                                    type='text'
                                    id='region'
                                    placeholder='Select region'
                                    className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                            <div className='w-full lg:w-64'>
                                <label
                                    htmlFor='state'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    State
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={states}
                                    isMulti
                                    onChange={handleStateChange}
                                    value={selectedStates}
                                    isLoading={isLoadingStates}
                                    placeholder='Select states...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div className='w-full lg:w-32'>
                                <label
                                    htmlFor='zip-code'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Zip Code
                                </label>
                                <input
                                    type='text'
                                    id='zip-code'
                                    name='zip_code'
                                    placeholder='Zip'
                                    value={formValues.zip_code}
                                    onChange={(e) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            zip_code: e.target.value,
                                        }))
                                    }
                                    className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                            <div className='w-full lg:w-48'>
                                <label
                                    htmlFor='city'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    City
                                </label>
                                <input
                                    type='text'
                                    id='city'
                                    name='city'
                                    placeholder='City'
                                    value={formValues.city}
                                    onChange={(e) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            city: e.target.value,
                                        }))
                                    }
                                    className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                        </div>

                        {/* Row 2b: Condition, diagnosis and referral source */}
                        <div className='mt-4 flex flex-col gap-4 lg:flex-row'>
                            <div className='w-full lg:w-64'>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Condition
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={medicalReasonOptions}
                                    isMulti
                                    onChange={handleMedicalReasonChange}
                                    value={selectedMedicalReasons}
                                    isLoading={isLoadingCscs}
                                    placeholder='Select conditions...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div className='w-full lg:w-80'>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Diagnosis
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={availableMedicalIssues}
                                    isMulti
                                    onChange={handleMedicalIssueChange}
                                    value={selectedMedicalIssues}
                                    isLoading={isLoadingCscs}
                                    placeholder='Select diagnoses...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div className='w-full lg:w-64'>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Referred By
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={referredByOptions}
                                    isMulti
                                    onChange={(
                                        options: MultiValue<StateOption>,
                                    ) => setSelectedReferredBy([...options])}
                                    value={selectedReferredBy}
                                    isLoading={isLoadingCscs}
                                    placeholder='Select sources...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 3: Privacy, Living Donor, CSC, Special Circumstance */}
                        <div className='mt-4 flex flex-col items-end gap-4 lg:flex-row'>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Privacy
                                </label>
                                <div className='flex h-10 items-center gap-3 text-sm'>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='privacy'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value=''
                                            checked={formValues.privacy === ''}
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    privacy: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Any
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='privacy'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='private'
                                            checked={
                                                formValues.privacy === 'private'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    privacy: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Private
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='privacy'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='public'
                                            checked={
                                                formValues.privacy === 'public'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    privacy: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Public
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Living Donor
                                </label>
                                <div className='flex h-10 items-center gap-3 text-sm'>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='living_donor'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value=''
                                            checked={
                                                formValues.living_donor === ''
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    living_donor:
                                                        e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Any
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='living_donor'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='true'
                                            checked={
                                                formValues.living_donor ===
                                                'true'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    living_donor:
                                                        e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Yes
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='living_donor'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='false'
                                            checked={
                                                formValues.living_donor ===
                                                'false'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    living_donor:
                                                        e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        No
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Deceased
                                </label>
                                <div className='flex h-10 items-center gap-3 text-sm'>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='deceased'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value=''
                                            checked={
                                                formValues.deceased === ''
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    deceased: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Any
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='deceased'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='true'
                                            checked={
                                                formValues.deceased === 'true'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    deceased: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        Yes
                                    </label>
                                    <label className='flex cursor-pointer items-center gap-1'>
                                        <input
                                            name='deceased'
                                            className='accent-[#00adb3]'
                                            type='radio'
                                            value='false'
                                            checked={
                                                formValues.deceased === 'false'
                                            }
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    deceased: e.target.value,
                                                }))
                                            }
                                        />{' '}
                                        No
                                    </label>
                                </div>
                            </div>
                            <div className='w-full lg:w-48'>
                                <label
                                    htmlFor='csc'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    CSC
                                </label>
                                <Select
                                    id='csc'
                                    menuPlacement='auto'
                                    options={cscOptions}
                                    isClearable
                                    isSearchable
                                    isLoading={isLoadingCscs}
                                    value={selectedCsc}
                                    onChange={(option) => {
                                        setSelectedCsc(
                                            option as StateOption | null,
                                        );
                                        setFormValues((prev) => ({
                                            ...prev,
                                            csc:
                                                (option as StateOption | null)
                                                    ?.value || '',
                                        }));
                                    }}
                                    placeholder='Select CSC'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div className='w-full lg:w-48'>
                                <label
                                    htmlFor='special-circumstance'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Special Circumstance
                                </label>
                                <Select
                                    id='special-circumstance'
                                    menuPlacement='auto'
                                    options={[]}
                                    isClearable
                                    value={null}
                                    onChange={(option) =>
                                        setFormValues((prev) => ({
                                            ...prev,
                                            special_circumstance:
                                                option?.value || '',
                                        }))
                                    }
                                    placeholder='Select'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 3: Campaign Created Date Range */}
                        <div className='mt-4'>
                            <label className='mb-1 block text-[12px] font-bold'>
                                Campaign Created Date Range
                            </label>
                            <div className='flex flex-wrap items-center gap-2'>
                                <div className='w-56'>
                                    <Select
                                        menuPlacement='auto'
                                        options={datePresetSelectOptions}
                                        isClearable
                                        value={
                                            datePresetSelectOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    selectedCreatedDatePreset,
                                            ) || null
                                        }
                                        onChange={(option) => {
                                            const presetValue =
                                                option?.value || '';
                                            setSelectedCreatedDatePreset(
                                                presetValue,
                                            );
                                            const preset = datePresets.find(
                                                (p) => p.value === presetValue,
                                            );
                                            if (preset) {
                                                const range = preset.getRange();
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    campaign_created_start_date:
                                                        range.start,
                                                    campaign_created_end_date:
                                                        range.end,
                                                }));
                                            }
                                        }}
                                        placeholder='Select date range'
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                            }),
                                        }}
                                        classNames={{
                                            control: () =>
                                                'rounded border-gray-400 text-sm',
                                            placeholder: () => 'text-gray-400',
                                        }}
                                    />
                                </div>
                                <input
                                    type='date'
                                    name='campaign_created_start_date'
                                    value={
                                        formValues.campaign_created_start_date
                                    }
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            campaign_created_start_date:
                                                e.target.value,
                                        }));
                                        setSelectedCreatedDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                                <span className='text-sm text-gray-500'>
                                    to
                                </span>
                                <input
                                    type='date'
                                    name='campaign_created_end_date'
                                    value={formValues.campaign_created_end_date}
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            campaign_created_end_date:
                                                e.target.value,
                                        }));
                                        setSelectedCreatedDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                        </div>

                        {/* Row 4: Campaign Open Date Range */}
                        <div className='mt-4'>
                            <label className='mb-1 block text-[12px] font-bold'>
                                Campaign Open Date Range
                            </label>
                            <div className='flex flex-wrap items-center gap-2'>
                                <div className='w-56'>
                                    <Select
                                        menuPlacement='auto'
                                        options={datePresetSelectOptions}
                                        isClearable
                                        value={
                                            datePresetSelectOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    selectedFundedDatePreset,
                                            ) || null
                                        }
                                        onChange={(option) => {
                                            const presetValue =
                                                option?.value || '';
                                            setSelectedFundedDatePreset(
                                                presetValue,
                                            );
                                            const preset = datePresets.find(
                                                (p) => p.value === presetValue,
                                            );
                                            if (preset) {
                                                const range = preset.getRange();
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    campaign_funded_start_date:
                                                        range.start,
                                                    campaign_funded_end_date:
                                                        range.end,
                                                }));
                                            }
                                        }}
                                        placeholder='Select date range'
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                            }),
                                        }}
                                        classNames={{
                                            control: () =>
                                                'rounded border-gray-400 text-sm',
                                            placeholder: () => 'text-gray-400',
                                        }}
                                    />
                                </div>
                                <input
                                    type='date'
                                    name='campaign_funded_start_date'
                                    value={
                                        formValues.campaign_funded_start_date
                                    }
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            campaign_funded_start_date:
                                                e.target.value,
                                        }));
                                        setSelectedFundedDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                                <span className='text-sm text-gray-500'>
                                    to
                                </span>
                                <input
                                    type='date'
                                    name='campaign_funded_end_date'
                                    value={formValues.campaign_funded_end_date}
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            campaign_funded_end_date:
                                                e.target.value,
                                        }));
                                        setSelectedFundedDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                        </div>

                        {/* Row 4b: Any status date, as a range */}
                        <div className='mt-4'>
                            <label className='mb-1 block text-[12px] font-bold'>
                                Status Date Range
                            </label>
                            <div className='flex flex-wrap items-center gap-2'>
                                <div className='w-56'>
                                    <Select
                                        menuPlacement='auto'
                                        options={STATUS_DATE_FIELD_OPTIONS}
                                        isClearable
                                        value={
                                            STATUS_DATE_FIELD_OPTIONS.find(
                                                (opt) =>
                                                    opt.value ===
                                                    formValues.status_date_field,
                                            ) || null
                                        }
                                        onChange={(option) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                status_date_field:
                                                    option?.value || '',
                                            }))
                                        }
                                        placeholder='Select date...'
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                            }),
                                        }}
                                        classNames={{
                                            control: () =>
                                                'rounded border-gray-400 text-sm',
                                            placeholder: () => 'text-gray-400',
                                        }}
                                    />
                                </div>
                                <div className='w-56'>
                                    <Select
                                        menuPlacement='auto'
                                        options={datePresetSelectOptions}
                                        isClearable
                                        value={
                                            datePresetSelectOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    selectedStatusDatePreset,
                                            ) || null
                                        }
                                        onChange={(option) => {
                                            const presetValue =
                                                option?.value || '';
                                            setSelectedStatusDatePreset(
                                                presetValue,
                                            );
                                            const preset = datePresets.find(
                                                (p) => p.value === presetValue,
                                            );
                                            if (preset) {
                                                const range = preset.getRange();
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    status_date_start:
                                                        range.start,
                                                    status_date_end: range.end,
                                                }));
                                            }
                                        }}
                                        placeholder='Select date range'
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                            }),
                                        }}
                                        classNames={{
                                            control: () =>
                                                'rounded border-gray-400 text-sm',
                                            placeholder: () => 'text-gray-400',
                                        }}
                                    />
                                </div>
                                <input
                                    type='date'
                                    name='status_date_start'
                                    value={formValues.status_date_start}
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            status_date_start: e.target.value,
                                        }));
                                        setSelectedStatusDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                                <span className='text-sm text-gray-500'>
                                    to
                                </span>
                                <input
                                    type='date'
                                    name='status_date_end'
                                    min={
                                        formValues.status_date_start ||
                                        undefined
                                    }
                                    value={formValues.status_date_end}
                                    onChange={(e) => {
                                        setFormValues((prev) => ({
                                            ...prev,
                                            status_date_end: e.target.value,
                                        }));
                                        setSelectedStatusDatePreset('');
                                    }}
                                    className='h-10 rounded border border-gray-400 px-3 text-sm'
                                />
                            </div>
                            <p className='mt-1 text-[11px] text-gray-500'>
                                Finds campaigns that reached the selected
                                milestone in this range, whatever their status
                                is today.
                            </p>
                        </div>

                        {/* Row 5: Funds Raised Amount and Unique Donors */}
                        <div className='mt-4 flex flex-col gap-6 lg:flex-row'>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Funds Raised Amount
                                </label>
                                <div className='flex items-center gap-2'>
                                    <div className='relative'>
                                        <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500'>
                                            $
                                        </span>
                                        <input
                                            type='number'
                                            name='funds_raised_min'
                                            placeholder='Min'
                                            value={formValues.funds_raised_min}
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    funds_raised_min:
                                                        e.target.value,
                                                }))
                                            }
                                            className='h-10 w-28 rounded border border-gray-400 pl-7 pr-3 text-sm'
                                            min='0'
                                        />
                                    </div>
                                    <span className='text-sm text-gray-500'>
                                        to
                                    </span>
                                    <div className='relative'>
                                        <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500'>
                                            $
                                        </span>
                                        <input
                                            type='number'
                                            name='funds_raised_max'
                                            placeholder='Max'
                                            value={formValues.funds_raised_max}
                                            onChange={(e) =>
                                                setFormValues((prev) => ({
                                                    ...prev,
                                                    funds_raised_max:
                                                        e.target.value,
                                                }))
                                            }
                                            className='h-10 w-28 rounded border border-gray-400 pl-7 pr-3 text-sm'
                                            min='0'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Unique Donors
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='number'
                                        name='unique_donors_min'
                                        placeholder='Min'
                                        value={formValues.unique_donors_min}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                unique_donors_min:
                                                    e.target.value,
                                            }))
                                        }
                                        className='h-10 w-24 rounded border border-gray-400 px-3 text-sm'
                                        min='0'
                                    />
                                    <span className='text-sm text-gray-500'>
                                        to
                                    </span>
                                    <input
                                        type='number'
                                        name='unique_donors_max'
                                        placeholder='Max'
                                        value={formValues.unique_donors_max}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                unique_donors_max:
                                                    e.target.value,
                                            }))
                                        }
                                        className='h-10 w-24 rounded border border-gray-400 px-3 text-sm'
                                        min='0'
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Row 6: Demographics */}
                        <div className='mt-4 flex flex-col gap-6 lg:flex-row'>
                            <div className='w-full lg:w-72'>
                                <label
                                    htmlFor='ethnicity'
                                    className='mb-1 block text-[12px] font-bold'
                                >
                                    Ethnicity
                                </label>
                                <Select
                                    id='ethnicity'
                                    menuPlacement='auto'
                                    options={ethnicityOptions}
                                    isMulti
                                    onChange={handleEthnicityChange}
                                    value={selectedEthnicities}
                                    placeholder='Select ethnicities...'
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                        }),
                                    }}
                                    classNames={{
                                        control: () =>
                                            'rounded border-gray-400 text-sm',
                                        placeholder: () => 'text-gray-400',
                                    }}
                                />
                            </div>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Date of Birth
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='date'
                                        name='dob_start_date'
                                        value={formValues.dob_start_date}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                dob_start_date: e.target.value,
                                            }))
                                        }
                                        className='h-10 rounded border border-gray-400 px-3 text-sm'
                                    />
                                    <span className='text-sm text-gray-500'>
                                        to
                                    </span>
                                    <input
                                        type='date'
                                        name='dob_end_date'
                                        value={formValues.dob_end_date}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                dob_end_date: e.target.value,
                                            }))
                                        }
                                        className='h-10 rounded border border-gray-400 px-3 text-sm'
                                    />
                                </div>
                            </div>
                            <div>
                                <label className='mb-1 block text-[12px] font-bold'>
                                    Age
                                </label>
                                <div className='flex items-center gap-2'>
                                    <input
                                        type='number'
                                        name='age_min'
                                        placeholder='Min'
                                        value={formValues.age_min}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                age_min: e.target.value,
                                            }))
                                        }
                                        className='h-10 w-24 rounded border border-gray-400 px-3 text-sm'
                                        min='0'
                                        max='120'
                                    />
                                    <span className='text-sm text-gray-500'>
                                        to
                                    </span>
                                    <input
                                        type='number'
                                        name='age_max'
                                        placeholder='Max'
                                        value={formValues.age_max}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                age_max: e.target.value,
                                            }))
                                        }
                                        className='h-10 w-24 rounded border border-gray-400 px-3 text-sm'
                                        min='0'
                                        max='120'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Column Configuration */}
                    <div className='mt-8 border-t border-gray-200 pt-6'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setShowColumns((s) => !s)}
                                className='flex items-center gap-2 text-[12px] font-bold text-[#949599]'
                            >
                                COLUMNS ({selectedColumns.length} selected)
                                {showColumns ? (
                                    <FaMinus size={10} />
                                ) : (
                                    <FaPlus size={10} />
                                )}
                            </button>
                            {!isDefaultSelection(selectedColumns) && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSelectedColumns(DEFAULT_VISIBLE_COLUMNS)
                                    }
                                    className='text-[12px] text-gray-500 underline hover:text-gray-700'
                                >
                                    Reset to default
                                </button>
                            )}
                        </div>
                        {showColumns && (
                            <div className='mt-4 space-y-5'>
                                {COLUMNS_BY_GROUP.map(({ group, columns }) => (
                                    <div key={group}>
                                        <div className='mb-2 text-[11px] font-bold uppercase tracking-wide text-[#949599]'>
                                            {group}
                                        </div>
                                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                                            {columns.map((column) => (
                                                <label
                                                    key={column.key}
                                                    className='flex cursor-pointer items-center gap-2 text-sm'
                                                >
                                                    <input
                                                        type='checkbox'
                                                        checked={selectedColumns.includes(
                                                            column.key,
                                                        )}
                                                        onChange={() =>
                                                            toggleColumn(column.key)
                                                        }
                                                        className='rounded border-gray-300 text-primary focus:ring-primary'
                                                    />
                                                    <span className='text-gray-700'>
                                                        {column.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Search Campaigns
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CampaignAccordionItem;
